import {
    AfterViewInit,
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    ViewChild,
    computed,
    inject,
    input,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { fromEvent } from 'rxjs';
import { debounceTime, filter, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApplyPipe } from 'ngxtension/call-apply';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faFile, faFolder } from '@fortawesome/pro-solid-svg-icons';

export type TypeaheadSearchResult = {
    [key: string]: unknown;
    disabled?: boolean;
};

/**
 * Shared typeahead form that can enable selection of items from text input.
 *
 * Inputs: such as `valueFormControl` for form data and `displayFormControl` for the UI.
 *
 * Outputs: such as `search` on keyup events, result selection with `resultSelection`,
 * and `clearForm` for form validation in parent.
 */
@Component({
    selector: 'r1-compass-lib-typeahead-search',
    templateUrl: './typeahead-search.component.html',
    imports: [
        MatAutocompleteModule,
        MatFormFieldModule,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        ApplyPipe,
        FontAwesomeModule,
    ],
    styleUrl: './typeahead-search.component.scss',
})
export class TypeaheadSearchComponent implements AfterViewInit, OnChanges {
    readonly placeholder = input<string>();

    /** `FormControl` to be used to persist data */
    @Input({ required: true })
    valueFormControl!: FormControl;

    /** `FormControl` to be used to display the selected field. Make sure to reset in parent on the search event */
    @Input({ required: true })
    displayFormControl!: FormControl;

    searchResults = input.required<TypeaheadSearchResult[]>();

    /** Path of property on a result object to be used as display in dropdown list */
    readonly resultDisplayPropertyPath = input.required<string>();

    /** Path of property on a result object to be used as the value when persisting data */
    readonly resultValuePropertyPath = input.required<string>();

    /** Whether or not the typeahead field should be disabled */
    readonly disabled = input.required<boolean>();

    /** Introduced in discussion of 9098 (o2c appender), this optional field can set a minimum character threshhold before searching */
    readonly minimumCharactersToSearch = input<number | undefined>(0);

    /** Event triggered on keyup event */
    @Output()
    search: EventEmitter<string> = new EventEmitter<string>();

    /** Event triggered when a result is selected */
    @Output()
    resultSelection: EventEmitter<MatAutocompleteSelectedEvent> = new EventEmitter<MatAutocompleteSelectedEvent>(); //

    /** Event triggered when the search form is cleared. Gives parent components ability to validate form field */
    @Output()
    clearForm: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild('searchInput')
    searchElement!: ElementRef<HTMLInputElement>;

    readonly #destroyRef = inject(DestroyRef);

    constructor() {
        const library = inject(FaIconLibrary);

        library.addIcons(faFile, faFolder);
    }

    /**
     * @description
     * (Optional) If icons should be displayed, provide: \
     *            (1) desired icon name (and register it in `library.addIcons(...)` of this component) \
     *            (2) field that is unique to a given search result
     */
    icons = input<{ matchingField: string; icon: IconName }[]>([]);

    searchResultIcons = computed(() =>
        this.searchResults().map(result => this.icons()?.find(icon => Object.keys(result).includes(icon.matchingField)))
    );

    ngAfterViewInit() {
        if (this.searchElement) {
            fromEvent(this.searchElement.nativeElement, 'keyup')
                .pipe(
                    filter(
                        results =>
                            (results.target as HTMLInputElement).value.length >= (this.minimumCharactersToSearch() ?? 0)
                    )
                )
                .pipe(
                    debounceTime(300),
                    tap(results => {
                        const input = results.target as HTMLInputElement;
                        if (input.value !== '') {
                            this.search.emit(input.value);
                        } else {
                            this.clearForm.emit();
                        }
                    }),
                    takeUntilDestroyed(this.#destroyRef)
                )
                .subscribe();
        }
    }

    ngOnChanges() {
        this.disabled() ? this.displayFormControl.disable() : this.displayFormControl.enable();
    }

    onResultSelection(event: MatAutocompleteSelectedEvent) {
        const resultValuePropertyPath = this.resultValuePropertyPath();
        if (resultValuePropertyPath) {
            this.valueFormControl.setValue(this.getDescendantProp(event.option.value, resultValuePropertyPath));
        } else {
            this.valueFormControl.setValue(event.option.value);
        }

        const resultDisplayPropertyPath = this.resultDisplayPropertyPath();
        if (resultDisplayPropertyPath) {
            this.displayFormControl.setValue(this.getDescendantProp(event.option.value, resultDisplayPropertyPath));
        } else {
            this.displayFormControl.setValue(event.option.value);
        }
        this.resultSelection.emit(event);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getDescendantProp = (resultObjInitial: any, path: string) => {
        return path.split('.').reduce((accumulator, currentVal) => accumulator?.[currentVal], resultObjInitial);
    };
}
